import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { 
    ArrowLeft, ArrowRight, Loader2, Check, Sparkles, Crosshair, Star
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyCandidate = () => ({ tarea: '', impacto: 5, resistencia: 5, frecuencia_evitacion: 5, claridad: 5 });

export default function Modulo34TareaFaro() {
    const [candidatas, setCandidatas] = useState([emptyCandidate(), emptyCandidate(), emptyCandidate()]);
    const [seleccionada, setSeleccionada] = useState(-1);
    const [justificacion, setJustificacion] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');

    useEffect(() => {
        const loadDraft = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/tarea_faro_3`, { withCredentials: true });
                if (data.data) {
                    if (data.data.candidatas) setCandidatas(data.data.candidatas);
                    if (data.data.seleccionada !== undefined) setSeleccionada(data.data.seleccionada);
                    if (data.data.justificacion) setJustificacion(data.data.justificacion);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch { /* no draft */ } finally { setLoadingDraft(false); }
        };
        loadDraft();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'tarea_faro_3', data: { candidatas, seleccionada, justificacion } }, { withCredentials: true });
        } catch { /* silent */ } finally { setSavingDraft(false); }
    }, [candidatas, seleccionada, justificacion]);

    useEffect(() => {
        if (loadingDraft || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [candidatas, seleccionada, justificacion, saveDraft, loadingDraft, readOnly]);

    const updateCandidate = (idx, field, value) => {
        setCandidatas(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
    };

    const val = (v) => typeof v === 'object' ? v[0] : v;

    const allFilled = candidatas.every(c => c.tarea.trim().length > 0);
    const canSubmit = !readOnly && (allFilled && seleccionada >= 0 && justificacion.trim().length > 0);

    const getScore = (c) => {
        const imp = val(c.impacto);
        const res = val(c.resistencia);
        const freq = val(c.frecuencia_evitacion);
        const cla = val(c.claridad);
        return ((imp * 3) + (res * 2) + freq + cla) / 7;
    };

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) { toast.error('Evalúa las 3 tareas, selecciona una y justifica'); return; }
        setLoading(true);
        try {
            const cleaned = candidatas.map(c => ({
                tarea: c.tarea, impacto: val(c.impacto), resistencia: val(c.resistencia),
                frecuencia_evitacion: val(c.frecuencia_evitacion), claridad: val(c.claridad)
            }));
            const { data } = await axios.post(`${API_URL}/api/user/modules/tarea-faro-3/submit`,
                { candidatas: cleaned, tarea_seleccionada: candidatas[seleccionada].tarea, justificacion },
                { withCredentials: true });
            setAiInsight(data.ai_insight);
            setSubmitted(true);
            toast.success('Módulo completado');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl">
                    <CardContent className="pt-8 text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-[#5FAF9D] to-[#4A9A89] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                            <Crosshair className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Tarea faro seleccionada</h2>
                        <p className="text-lg font-medium text-[#3E5F6E] mb-4">"{candidatas[seleccionada]?.tarea}"</p>
                        {aiInsight && (
                            <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6">
                                <div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div>
                            </div>
                        )}
                        <div className="mb-6"><ModuleFeedback moduleId="tarea_faro_3" chapter={3} /></div>
                        <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl">Continuar<ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Capítulo 3</span>
                                    <span className="text-xs text-[#7A8A93]">Módulo 3.4</span>
                                </div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']">Selección de tarea faro</h1>
                            </div>
                        </div>
                        {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#3E5F6E]/5 to-transparent p-6">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-[#3E5F6E] to-[#2F4A57] rounded-xl flex items-center justify-center shadow-md">
                                <Crosshair className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-[#1F2A30]">Elige tu faro</CardTitle>
                                <p className="text-sm text-[#4A5B63]">Compara 3 tareas candidatas y selecciona la de mayor impacto</p>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6 space-y-6">
                        {candidatas.map((cand, idx) => (
                            <div key={idx} className={`p-5 rounded-2xl border-2 transition-all ${seleccionada === idx ? 'border-[#5FAF9D] bg-[#5FAF9D]/5 shadow-md' : 'border-[#DCE3E8] bg-[#F5F7F8]'}`}
                                data-testid={`candidate-${idx}`}>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${seleccionada === idx ? 'bg-[#5FAF9D] text-white' : 'bg-[#DCE3E8] text-[#7A8A93]'}`}>
                                            {idx + 1}
                                        </div>
                                        <input type="text" value={cand.tarea} onChange={e => updateCandidate(idx, 'tarea', e.target.value)}
                                            placeholder={`Tarea candidata ${idx + 1}`}
                                            className="text-sm font-medium border-b border-[#DCE3E8] bg-transparent focus:border-[#5FAF9D] outline-none py-1 flex-1" />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-[#7A8A93]">Score:</span>
                                        <span className="text-lg font-bold text-[#3E5F6E]">{getScore(cand).toFixed(1)}</span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {[
                                        { key: 'impacto', label: 'Impacto', color: '#5FAF9D' },
                                        { key: 'resistencia', label: 'Resistencia', color: '#D07F7E' },
                                        { key: 'frecuencia_evitacion', label: 'Evitación', color: '#E8B27A' },
                                        { key: 'claridad', label: 'Claridad', color: '#3E5F6E' }
                                    ].map(dim => (
                                        <div key={dim.key}>
                                            <div className="flex justify-between mb-1">
                                                <Label className="text-xs text-[#7A8A93]">{dim.label}</Label>
                                                <span className="text-xs font-bold" style={{ color: dim.color }}>{val(cand[dim.key])}</span>
                                            </div>
                                            <Slider value={[val(cand[dim.key])]} onValueChange={v => updateCandidate(idx, dim.key, v[0])} max={10} min={0} step={1} />
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 flex justify-end">
                                    <button onClick={() => setSeleccionada(idx)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${seleccionada === idx ? 'bg-[#5FAF9D] text-white shadow-md' : 'bg-white text-[#3E5F6E] border border-[#DCE3E8] hover:border-[#5FAF9D]'}`}
                                        data-testid={`select-candidate-${idx}`}>
                                        <Star className={`h-4 w-4 ${seleccionada === idx ? 'fill-current' : ''}`} />
                                        {seleccionada === idx ? 'Seleccionada' : 'Seleccionar'}
                                    </button>
                                </div>
                            </div>
                        ))}

                        {seleccionada >= 0 && (
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-[#3E5F6E]">¿Por qué elegiste esta tarea como tu faro?</Label>
                                <Textarea value={justificacion} onChange={e => setJustificacion(e.target.value)}
                                    placeholder="Explica por qué esta tarea merece ser tu prioridad..."
                                    className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                    data-testid="justificacion-input" />
                            </div>
                        )}

                        <div className="flex justify-end pt-6 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading}
                                className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl shadow-md"
                                data-testid="submit-tarea-faro">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Completar módulo</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
